import { api } from '../../functions/Apiwithouttoken';
import { formatNumber } from '../../utils/formatter';
import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback, memo } from 'react';
import { AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { trendStyle, EndDot } from './chartSeriesStyle';
import { ChevronDown } from 'lucide-react';

const timeIntervals = [
  { value: '1W', label: '1W' },
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: '6M', label: '6M' },
  { value: '1Y', label: '1Y' },
  { value: 'ALL', label: 'ALL' }
];

// Map city IDs to display names
const cityDisplayNames = {
  'dubai': 'Dubai',
  'los-angeles': 'Los Angeles',
  'new-york': 'New York',
  'chicago': 'Chicago',
  'washington': 'Washington',
  'pittsburgh': 'Pittsburgh',
  'miami-beach': 'Miami Beach',
  'boston': 'Boston',
  'brooklyn': 'Brooklyn',
  'austin': 'Austin',
  'denver': 'Denver'
};

// Pre-calculate interval offsets in days for faster filtering
const INTERVAL_DAYS = {
  '1W': 7,
  '1M': 30,
  '3M': 90,
  '6M': 180,
  '1Y': 365,
  'ALL': Infinity
};

// Mobile Skeleton Component
const MobileChartSkeleton = () => (
  <div className="md:hidden">
    <div className="h-80 animate-pulse">
      <div className="h-full bg-gray-200 rounded-lg mx-4 mt-4"></div>
    </div>
  </div>
);

// Desktop Skeleton Component  
const DesktopChartSkeleton = ({ cityName }) => (
  <div className="hidden md:block">
    <div className="w-full bg-white p-6 rounded-xl shadow-sm">
      <div className="flex justify-between items-center mb-6 animate-pulse">
        <div>
          <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-64"></div>
        </div>
        <div className="h-10 bg-gray-200 rounded w-20"></div>
      </div>
      <div className="h-96 bg-gray-200 rounded-lg animate-pulse"></div>
    </div>
  </div>
);

// Custom active dot matching design spec - purple dot with white border + outer ring
const CustomActiveDot = ({ cx, cy, color = "var(--cat-accent, #7A2BE4)" }) => {
  return (
    <g>
      <defs>
        <filter id="cityActiveDotShadow" x="-100%" y="-100%" width="300%" height="300%">
          <feDropShadow dx="0" dy="4" stdDeviation="10" floodColor="#000000" floodOpacity="0.2" />
        </filter>
      </defs>
      {/* Outer ring: 18x18, 0.6px purple border */}
      <circle cx={cx} cy={cy} r={9} fill="white" stroke={color} strokeWidth={0.6} filter="url(#cityActiveDotShadow)" />
      {/* Inner dot: 10x10, 3px white border, purple fill */}
      <circle cx={cx} cy={cy} r={5} fill={color} stroke="var(--surface, #FFFFFF)" strokeWidth={3} />
    </g>
  );
};

/**
 * Chart glitch treatment, ported from sattacell (`client/src/index.css` and
 * `components/markets/ProbabilityChart.jsx`).
 *
 * Two effects, both scoped to the chart card — the originals were declared
 * globally there and would otherwise cover the whole app:
 *
 *   `.ce-glitch-line`  the series sweeps in left-to-right by animating
 *                      `stroke-dashoffset`. Recharts re-mounts the path when
 *                      the data changes, and a `key` on the wrapper forces it
 *                      on a timeframe change too, so the sweep replays.
 *
 *   `.ce-glitch-crt`   an SVG turbulence grain, as a pointer-events:none
 *                      pseudo-element so the tooltip and hover dot still work
 *                      through it. The CRT scanlines were dropped — they read
 *                      as banding on this card's light palette.
 *
 * `stroke-dasharray: 3000` is a length that comfortably exceeds any path this
 * chart draws; a value shorter than the path would leave the line dashed.
 */
const GLITCH_STYLES = `
  to   { stroke-dashoffset: 0; }
}
@keyframes ce-glitch-flicker {
  0%, 19.999%, 22%, 62.999%, 64%, 64.999%, 70%, 100% { opacity: 1; }
  20%, 21.999%, 63%, 63.999%, 65%, 69.999%           { opacity: .78; }
}


.ce-glitch-crt { position: relative; }
/* Grain — the same fractalNoise turbulence, inlined as a data URI. */
.ce-glitch-crt::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  z-index: 3;
  opacity: .035;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}
`;

const CityTradingView = forwardRef(({ 
  city = 'dubai',
  initialInterval = null,
  defaultInterval = '1M'
}, ref) => {
  const [data, setData] = useState([]);
  const [interval, setInterval] = useState(initialInterval || defaultInterval);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);

  const cityName = cityDisplayNames[city] || 'Dubai';
  
  // Use useMemo for filtered data - computed only when data or interval changes
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const days = INTERVAL_DAYS[interval] || 30;
    if (days === Infinity) return data;
    
    const now = Date.now();
    const cutoff = now - (days * 24 * 60 * 60 * 1000);
    
    const filtered = data.filter(item => new Date(item.date).getTime() >= cutoff);
    
    // Fallback to last 7 entries if no data in range
    return filtered.length > 0 ? filtered : data.slice(-Math.min(7, data.length));
  }, [data, interval]);

  // Fetch data function - optimized
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api('parcl/city-data', { cityId: city });
      
      if (response.success && response.data?.PriceData) {
        // Backend already deduplicates and sorts - just ensure prices are numbers
        const priceData = response.data.PriceData.map(item => ({
          date: item.date,
          price: typeof item.price === 'number' ? item.price : parseFloat(item.price)
        }));
        
        setData(priceData);
        setMetrics(response.data.metrics);
      } else {
        setData([]);
        setMetrics(null);
      }
    } catch (error) {
      console.error(`Error fetching ${city}:`, error);
      setData([]);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, [city]);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    handleIntervalChange: (newInterval) => setInterval(newInterval),
    getCurrentInterval: () => interval,
    refreshData: fetchData
  }), [interval, fetchData]);

  // Update interval when initialInterval prop changes (for mobile control)
  useEffect(() => {
    if (initialInterval && initialInterval !== interval) {
      setInterval(initialInterval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInterval]);

  // Fetch data when city changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // UTC-aware date formatting to avoid timezone issues
  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getUTCMonth()]} ${date.getUTCDate()}`;
  }, []);

  const formatYAxisPrice = useCallback((price) => {
    return formatNumber(price, { currency: true, minDecimals: 0, maxDecimals: 0 });
  }, []);

  // UTC-aware tooltip to show correct year
  const CustomTooltip = memo(({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const date = new Date(item.payload.date);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dateStr = `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
    return (
      <div className="bg-white p-2 md:p-3 shadow-lg rounded-lg border text-xs md:text-sm max-w-[150px] md:max-w-none">
        <p className="text-gray-600 truncate">{dateStr}</p>
        <p className="font-semibold">{formatNumber(item.value, { currency: true, minDecimals: 2, maxDecimals: 2 })}</p>
      </div>
    );
  });

  // Show skeleton while loading or when no data is available
  // The skeleton is for the FIRST load only. `fetchData` sets `loading` on every
  // poll, so gating the chart on it alone tore the whole thing down and put the
  // placeholder back each refresh — the series blanked, remounted, and replayed
  // its reveal on a timer. Once there is data on screen, a refresh updates it in
  // place and the chart never disappears.
  if ((loading && data.length === 0) || !filteredData || filteredData.length === 0) {
    return (
      <div className="city-chart-container">
        <MobileChartSkeleton />
        <DesktopChartSkeleton cityName={cityName} />
      </div>
    );
  }

  // Check if we're in mobile mode (controlled by parent)
  const isMobileControlled = initialInterval !== null;

  /* Direction colour for the visible range — see chartSeriesStyle. */
  const series = trendStyle(filteredData);

  return (
    <div className="city-chart-container">
      <style>{GLITCH_STYLES}</style>
      {/* Mobile View */}
      <div className="md:hidden flex justify-center">
        <div className="h-80 w-full ce-glitch-crt ce-chart-anim" key={`glitch-m-${interval}`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={filteredData}
              margin={{ top: 10, right: 15, left: 5, bottom: 10 }}
            >
              {/* Horizontals only, and barely there — enough to read a level across, not enough to compete with the series. */}
              <CartesianGrid stroke="var(--line-2)" strokeOpacity={0.6} vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fill: 'var(--ink-3)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                type="category"
                allowDataOverflow={false}
              />
              <YAxis
                tickFormatter={formatYAxisPrice}
                tick={{ fill: 'var(--ink-3)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                domain={['auto', 'auto']}
                width={50}
              />
              <Tooltip
                content={<CustomTooltip />}
                formatter={formatYAxisPrice}
                labelFormatter={formatDate}
                wrapperStyle={{ zIndex: 10, pointerEvents: 'none', maxWidth: '90vw' }}
              />
              {/* Filled area under the line — the trend colour fading to
                  nothing, so it reads as the line's own shadow rather than as
                  a second series. Kept faint: the stroke carries the chart. */}
              <defs>
                <linearGradient id="ceAreaFill-city" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={series.color} stopOpacity={0.22} />
                  <stop offset="55%" stopColor={series.color} stopOpacity={0.06} />
                  <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              {series.lastValue !== null && (
                <ReferenceLine y={series.lastValue} stroke={series.color} strokeOpacity={0.35} strokeWidth={1} />
              )}
              <Area
                type="monotone"
                dataKey="price"
                stroke={series.color}
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="url(#ceAreaFill-city)"
                style={{ filter: `drop-shadow(0 6px 14px ${series.glow})` }}
                dot={<EndDot lastIndex={filteredData.length - 1} color={series.color} />}
                activeDot={<CustomActiveDot color={series.color} />}
                isAnimationActive={true}
                  animationDuration={900}
                  animationEasing="ease-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden md:block">
        <div className="w-full bg-white p-6 rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                {cityName} Price Chart
              </h2>
              {metrics && (
                <p className="text-sm text-gray-500 mt-1">
                  Current Index Price: {metrics.indexPrice}
                </p>
              )}
            </div>
            
            {/* Only show interval selector on desktop if not mobile-controlled */}
            {!isMobileControlled && (
              <div className="relative">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="ce-menu-trigger"
                  style={{ background: "var(--cat-accent-soft, #F8F4FC)" }}
                >
                  <span className="text-sm font-medium" style={{ color: "var(--cat-accent, #7C2BE8)" }}>{interval}</span>
                  <ChevronDown className="w-4 h-4" style={{ color: "var(--cat-accent, #7C2BE8)" }} />
                </button>

                {isOpen && (
                  <div className="ce-menu" role="menu">
                    {timeIntervals.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        role="menuitemradio"
                        aria-checked={interval === item.value}
                        onClick={() => {
                          setInterval(item.value);
                          setIsOpen(false);
                        }}
                        className="ce-menu-item"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="h-96 ce-glitch-crt ce-chart-anim" key={`glitch-${interval}`}>
                <ResponsiveContainer width="100%" height="100%">
              <AreaChart 
                data={filteredData} 
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                {/* Horizontals only, and barely there — enough to read a level across, not enough to compete with the series. */}
                <CartesianGrid stroke="var(--line-2)" strokeOpacity={0.6} vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  tick={{ fill: 'var(--ink-3)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  type="category"
                  allowDataOverflow={false}
                />
                <YAxis 
                  tickFormatter={formatYAxisPrice}
                  tick={{ fill: 'var(--ink-3)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  domain={['auto', 'auto']}
                />
                <Tooltip 
                  content={<CustomTooltip />}
                  formatter={formatYAxisPrice}
                  labelFormatter={formatDate}
                />
                {/* Filled area under the line — the trend colour fading to
                    nothing, so it reads as the line's own shadow rather than as
                    a second series. Kept faint: the stroke carries the chart. */}
                <defs>
                  <linearGradient id="ceAreaFill-city" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={series.color} stopOpacity={0.22} />
                    <stop offset="55%" stopColor={series.color} stopOpacity={0.06} />
                    <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                {series.lastValue !== null && (
                <ReferenceLine y={series.lastValue} stroke={series.color} strokeOpacity={0.35} strokeWidth={1} />
              )}
              <Area
                  type="monotone"
                  dataKey="price"
                  stroke={series.color}
                  strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                  fill="url(#ceAreaFill-city)"
                  style={{ filter: `drop-shadow(0 6px 14px ${series.glow})` }}
                  dot={<EndDot lastIndex={filteredData.length - 1} color={series.color} />}
                  activeDot={<CustomActiveDot color={series.color} />}
                  isAnimationActive={true}
                  animationDuration={900}
                  animationEasing="ease-out"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
});

// Add display name for debugging
CityTradingView.displayName = 'CityTradingView';

export default CityTradingView;